import { useId } from "react";
import { FileDropTarget } from "@/components/FileDropTarget";

// BRK-S421 — the won-bid steps' one upload control and one checkbox, moved
// out of the won-bid screen (BRK-S184) when it became four steps.

/** A document held by a slot — only ever the server's pointer. */
export interface HeldDocument {
  documentId: string;
  filename: string;
}

/** One upload: the drop target while empty, the file's name once the SERVER
 *  has stored it, with Replace. */
export function WonBidUploadSlot({
  inputLabel,
  prompt,
  hint,
  accept,
  held,
  uploading,
  disabled = false,
  error,
  onFile,
}: {
  inputLabel: string;
  prompt: string;
  hint: string;
  accept: string;
  held: HeldDocument | null;
  uploading: boolean;
  /** Not ready for a file yet (the bond form's type is not chosen). */
  disabled?: boolean;
  error?: string;
  onFile: (file: File) => void;
}) {
  const inputId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {held ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-brk-border p-3 text-sm">
          <span className="min-w-0 break-all text-brk-text">
            <span className="text-brk-dim">Attached: </span>
            {held.filename}
          </span>
          <input
            id={inputId}
            type="file"
            accept={accept}
            aria-label={inputLabel}
            className="sr-only"
            disabled={uploading || disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = "";
            }}
          />
          <label
            htmlFor={inputId}
            className="cursor-pointer font-medium text-brk-accent hover:underline"
          >
            Replace
          </label>
        </div>
      ) : (
        <FileDropTarget
          prompt={prompt}
          hint={hint}
          inputLabel={inputLabel}
          accept={accept}
          multiple={false}
          disabled={uploading || disabled}
          onFiles={(files) => {
            const [file] = files;
            if (file) onFile(file);
          }}
        />
      )}
      {uploading && (
        <p role="status" className="text-sm text-brk-dim">
          Uploading…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function WonBidCheckbox({
  label,
  checked,
  disabled = false,
  note,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  /** One line under the label, read as the box's description. */
  note?: string;
  onChange?: (on: boolean) => void;
}) {
  const noteId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label className="flex items-center gap-2 text-sm text-brk-text">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 disabled:cursor-not-allowed"
          checked={checked}
          disabled={disabled}
          aria-describedby={note ? noteId : undefined}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        {label}
      </label>
      {note && (
        <p id={noteId} className="pl-6 text-sm text-brk-dim">
          {note}
        </p>
      )}
    </div>
  );
}
